import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Building2, FileText, X, Star, 
  ShieldCheck, ChevronRight, Calendar, 
  CheckCircle2, Wallet, Sparkles, Plus, 
  Heart, ClipboardList, Info
} from "lucide-react";
import { useAuth } from "../App";
import { useProfile, useBookings, useWallet, useFavourites, useLoyalty, useMembership, useToggleFavourite, useCancelBooking, useToast, useTopup } from "../api/hooks";
import { processPayment } from "../api/razorpay";
import Footer from "../components/Footer";
import Bokeh  from "../components/Bokeh";
import { Stars, Toast, BtnGold, BtnGhost, Spinner, Badge } from "../components/UI";
import { T, GLOBAL_STYLES } from "../tokens";
import { useTranslation } from "../i18n";

function BookingCard({ booking, onCancel, cancelling }) {
  const navigate = useNavigate();
  const STATUS = { pending:{color:T.goldLt,label:"Pending"}, confirmed:{color:T.green,label:"Confirmed"}, in_progress:{color:"#7AB8E8",label:"In Progress"}, completed:{color:T.green,label:"Completed"}, cancelled:{color:T.red,label:"Cancelled"}, no_show:{color:T.red,label:"No-Show"} };
  const sc = STATUS[booking.status]||STATUS.pending;
  return (
    <div style={{background:T.dark3,border:`1px solid ${T.goldBdr}`,borderRadius:14,overflow:"hidden",marginBottom:14}}>
      <div style={{display:"flex"}}>
        <div style={{width:150,flexShrink:0,background:"linear-gradient(160deg,#3D2B1A,#1A1008)",position:"relative",minHeight:130,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <Bokeh count={6}/>
          <Building2 size={48} color={T.gold} style={{ opacity: 0.25, position: "relative", zIndex: 1 }} />
        </div>
        <div style={{flex:1,padding:"16px 18px"}}>
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:6}}>
            <div>
              <div style={{fontFamily:"Cormorant Garamond,serif",fontSize:19,fontWeight:600,color:T.text}}>{booking.salon?.name||"Salon"}</div>
              <div style={{fontSize:12,color:T.muted}}>{booking.salon?.area}, {booking.salon?.city}</div>
            </div>
            <div style={{fontFamily:"Cormorant Garamond,serif",fontSize:20,fontWeight:600,color:T.goldLt}}>₹{Number(booking.total_amount).toLocaleString("en-IN")}</div>
          </div>
          <div style={{fontSize:13,color:T.text,marginBottom:6}}>{booking.services?.map(s=>s.name).join(", ")}</div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <span style={{background:`${sc.color}18`,border:`1px solid ${sc.color}55`,color:sc.color,padding:"3px 10px",borderRadius:10,fontSize:11.5,fontWeight:600}}>{sc.label}</span>
            <span style={{fontSize:12,color:T.muted,display:"flex",alignItems:"center",gap:6}}>
              <Calendar size={12} color={T.gold} /> {new Date(booking.booking_date).toLocaleDateString("en-IN",{weekday:"short",day:"numeric",month:"short"})} · {booking.start_time?.slice(0,5)}
            </span>
          </div>
        </div>
      </div>
      {["pending","confirmed"].includes(booking.status)&&(
        <div style={{display:"flex",borderTop:`1px solid ${T.goldBdr}`}}>
          {[
            { icon: <FileText size={14} />, label: "Details", fn: () => navigate(`/bookings/${booking.booking_ref}`) },
            { icon: <ClipboardList size={14} />, label: "Reschedule", fn: () => navigate(`/bookings/${booking.booking_ref}`) }
          ].map((item, idx)=>(
            <button key={item.label} onClick={item.fn} style={{flex:1,padding:11,background:"transparent",border:"none",borderRight:idx===0?`1px solid ${T.goldBdr}`:"none",color:T.muted,fontSize:12,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              {item.icon} {item.label}
            </button>
          ))}
          <button onClick={()=>onCancel(booking.booking_ref)} disabled={cancelling} style={{flex:1,padding:11,background:"transparent",border:"none",borderLeft:`1px solid ${T.goldBdr}`,color:T.red,fontSize:12,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            {cancelling ? <Spinner size={14}/> : <><X size={14} /> Cancel</>}
          </button>
        </div>
      )}
      {booking.status==="completed"&&!booking.review&&(
        <div style={{display:"flex",borderTop:`1px solid ${T.goldBdr}`}}>
          <button onClick={()=>navigate(`/bookings/${booking.booking_ref}`)} style={{flex:1,padding:11,background:T.goldDim,border:"none",color:T.goldLt,fontSize:12.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
            <Star size={16} fill={T.goldLt} /> Write a Review
          </button>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isImpersonating = !!localStorage.getItem("fyz_admin_session");
  const { t } = useTranslation();
  const { msg, visible, show } = useToast();
  const [topupModal, setTopupModal] = useState(false);
  const [topupAmt, setTopupAmt]     = useState(500);
  const { mutate: doTopup, isPending: toppingUp } = useTopup();
  const { data: profile    } = useProfile();
  const { data: walletData } = useWallet();
  const { data: upcoming   } = useBookings({ status:"confirmed", per_page:3 });
  const { data: past       } = useBookings({ status:"completed", per_page:3 });
  const { data: favs       } = useFavourites();
  const { data: membership } = useMembership();
  const { data: loyalty    } = useLoyalty();
  const { mutate: cancelBooking, isPending: cancelling } = useCancelBooking();
  const { mutate: toggleFav } = useToggleFavourite();

  const upcomingList = upcoming?.data?.data || [];
  const pastList     = past?.data?.data     || [];
  const favList      = favs?.data?.data || favs?.data || [];
  const walletBal    = walletData?.data?.data?.balance || walletData?.data?.balance || 0;
  const loyaltyPts   = profile?.data?.data?.loyalty_points || profile?.data?.loyalty_points || user?.loyalty_points || 0;
  const memberTier   = membership?.data?.data?.tier || membership?.data?.tier || user?.membership_tier || "none";

  const handleCancel = (ref) => {
    if (!window.confirm("Cancel this booking?")) return;
    cancelBooking({ ref, reason:"Cancelled by customer" }, {
      onSuccess: () => show("Booking cancelled."),
      onError:   (err) => show(err.message || "Cancel failed"),
    });
  };

  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <div style={{maxWidth:1240,margin:"0 auto",padding:"8px 40px",fontFamily:"'DM Sans',sans-serif",position:"relative"}}>
        <Bokeh count={14} style={{position:"fixed",inset:0,zIndex:0,pointerEvents:"none"}}/>
        <div style={{position:"relative",zIndex:1,display:"grid",gridTemplateColumns:"1fr 300px",gap:28}}>
          {/* Left */}
          <div>
            <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
              <h1 style={{ fontFamily: "Cormorant Garamond,serif", fontSize: 32, fontWeight: 400, color: T.text, margin: 0 }}>
                {t("dashboard.welcome", { name: "" })}<em style={{ color: T.goldLt, fontStyle: "italic" }}>{profile?.data?.data?.name?.split(" ")[0] || profile?.data?.name?.split(" ")[0] || user?.name?.split(" ")[0]}</em>
              </h1>

              {memberTier !== "none" && (
                <div 
                  onClick={() => navigate("/membership")}
                  style={{ 
                    display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", 
                    background: "linear-gradient(135deg, rgba(201,168,76,0.12), rgba(13,11,8,0.4))", 
                    border: `1px solid ${T.goldBdrS}`, borderRadius: 30, 
                    cursor: "pointer", transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    backdropFilter: "blur(8px)",
                  }}
                  onMouseOver={e => { e.currentTarget.style.borderColor = T.gold; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(201,168,76,0.15)"; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor = T.goldBdrS; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <ShieldCheck size={14} color={T.goldLt} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.goldLt, textTransform: "uppercase", letterSpacing: ".08em" }}>
                    Fayriza {memberTier}
                  </span>
                  <div style={{ width: 1, height: 12, background: "rgba(201,168,76,0.3)" }} />
                  <span style={{ fontSize: 11, color: T.goldLt, opacity: 0.8 }}>Manage</span>
                  <ChevronRight size={14} color={T.goldLt} style={{ opacity: 0.6 }} />
                </div>
              )}
            </div>
            {/* Upcoming */}
            <div style={{marginBottom:28}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${T.goldBdr}`,paddingBottom:12,marginBottom:16}}>
                <div style={{fontFamily:"Cormorant Garamond,serif",fontSize:22,fontWeight:600,color:T.text}}>{t("dashboard.upcoming_bookings")}</div>
                <span onClick={()=>navigate("/bookings")} style={{fontSize:12,color:T.muted,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                  {t("common.view_all")} <ChevronRight size={14} />
                </span>
              </div>
              {upcomingList.length===0?(
                <div style={{textAlign:"center",padding:"36px 20px",background:T.dark3,border:`1px solid ${T.goldBdr}`,borderRadius:14}}>
                  <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
                    <div style={{width:64,height:64,borderRadius:"50%",background:T.dark4,display:"flex",alignItems:"center",justifyContent:"center",color:T.muted}}>
                      <Calendar size={32} />
                    </div>
                  </div>
                  <div style={{fontSize:14,color:T.muted,marginBottom:14}}>{t("dashboard.no_upcoming")}</div>
                  <BtnGold size="sm" onClick={()=>navigate("/salons")}>{t("salons.book_now")}</BtnGold>
                </div>
              ):upcomingList.map(b=><BookingCard key={b.id} booking={b} onCancel={handleCancel} cancelling={cancelling}/>)}
            </div>
            {/* Stats */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:28}}>
              {[
                {icon:<Calendar size={20} />,label:"Upcoming",value:upcoming?.data?.data?.meta?.total||upcoming?.data?.meta?.total||0,path:"/bookings"},
                {icon:<CheckCircle2 size={20} />,label:"Completed",value:past?.data?.data?.meta?.total||past?.data?.meta?.total||0,path:"/bookings"},
                {icon:<Star size={20} />,label:"Loyalty Pts",value:loyaltyPts.toLocaleString("en-IN"),path:"/loyalty"},
                {icon:<Wallet size={20} />,label:"Wallet",value:`₹${Number(walletBal).toLocaleString("en-IN")}`,path:null}
              ].map(s=>(
                <div key={s.label} onClick={s.path?()=>navigate(s.path):undefined} style={{background:T.dark3,border:`1px solid ${T.goldBdr}`,borderRadius:12,padding:"18px 14px",textAlign:"center",cursor:s.path?"pointer":"default",transition:"all .22s"}}
                  onMouseOver={e=>{if(s.path){e.currentTarget.style.borderColor=T.gold;e.currentTarget.style.transform="translateY(-2px)";}}}
                  onMouseOut={e=>{e.currentTarget.style.borderColor=T.goldBdr;e.currentTarget.style.transform="";}}>
                  <div style={{color:T.gold,marginBottom:8,display:"flex",justifyContent:"center"}}>{s.icon}</div>
                  <div style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{s.label}</div>
                  <div style={{fontFamily:"Cormorant Garamond,serif",fontSize:26,fontWeight:600,color:T.goldLt}}>{s.value}</div>
                </div>
              ))}
            </div>
            {/* Gold promo */}
            <div style={{background:"linear-gradient(120deg,#2A1C08,#1C1208,#2A1C08)",border:`1px solid ${T.goldBdrS}`,borderRadius:14,padding:"24px 28px",position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",right:-20,top:-20,width:180,height:180,borderRadius:"50%",background:"radial-gradient(circle,rgba(201,168,76,.12) 0%,transparent 70%)",pointerEvents:"none"}}/>
              <div style={{fontFamily:"Cormorant Garamond,serif",fontSize:22,fontWeight:400,color:T.text,marginBottom:14}}>Get <span style={{color:T.goldLt,fontWeight:600}}>20% OFF</span> with Gold Membership</div>
              <BtnGold onClick={()=>navigate("/membership")}>Get Members Benefits</BtnGold>
            </div>
          </div>
          {/* Sidebar */}
          <div style={{display:"flex",flexDirection:"column",gap:20}}>
            <div style={{borderRadius:16,overflow:"hidden",position:"relative",height:240,background:"linear-gradient(160deg,#2A1A08,#0D0B08)",border:`1px solid ${T.goldBdr}`,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Bokeh count={14}/>
              <Sparkles size={90} color={T.gold} style={{ opacity: 0.3, position: "relative", zIndex: 1 }} />
            </div>
            {/* Wallet */}
            <div style={{background:T.dark3,border:`1px solid ${T.goldBdr}`,borderRadius:14,padding:16}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                <span style={{fontSize:12,color:T.muted,letterSpacing:".06em",textTransform:"uppercase",display:"flex",alignItems:"center",gap:6}}>
                  <Wallet size={14} /> {t("wallet.title")}
                </span>
                <span style={{fontFamily:"Cormorant Garamond,serif",fontSize:28,fontWeight:600,color:T.goldLt}}>₹{Number(walletBal).toLocaleString("en-IN")}</span>
              </div>
              <div style={{display:"flex",gap:8}}>
                <BtnGold 
                  full 
                  size="sm" 
                  disabled={isImpersonating}
                  onClick={() => isImpersonating ? show("Wallet Top Up is disabled during administrative sessions.") : setTopupModal(true)} 
                  style={{display:"flex",alignItems:"center",gap:6,justifyContent:"center", ...(isImpersonating ? { opacity: 0.5, cursor: "not-allowed" } : {})}}
                >
                  <Plus size={14} /> {t("wallet.topup")}
                </BtnGold>
                <BtnGhost size="sm" onClick={()=>navigate("/orders")}>{t("wallet.history")}</BtnGhost>
              </div>
            </div>
            {/* Favourites */}
            <div style={{background:T.dark3,border:`1px solid ${T.goldBdr}`,borderRadius:14,padding:16}}>
              <div style={{fontFamily:"Cormorant Garamond,serif",fontSize:18,fontWeight:600,color:T.text,marginBottom:10,paddingBottom:8,borderBottom:`1px solid ${T.goldBdr}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                {t("dashboard.favourites")} <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:11.5,color:T.muted,cursor:"pointer",fontWeight:400,display:"flex",alignItems:"center",gap:2}} onClick={()=>navigate("/salons")}>{t("common.view_all")} <ChevronRight size={12} /></span>
              </div>
              {favList.length===0?(
                <div style={{fontSize:13,color:T.muted,textAlign:"center",padding:"12px 0"}}>{t("dashboard.no_favourites")} <span onClick={()=>navigate("/salons")} style={{color:T.goldLt,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4}}>{t("dashboard.book_a_salon")} <ChevronRight size={14} /></span></div>
              ):favList.slice(0,3).map((s,i)=>(
                <div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:i<2?`1px solid rgba(201,168,76,.08)`:"none"}}>
                  <div style={{width:36,height:36,borderRadius:8,background:`linear-gradient(135deg,${T.goldDim},${T.dark4})`,border:`1px solid ${T.goldBdr}`,display:"flex",alignItems:"center",justifyContent:"center",color:T.gold,flexShrink:0}}>
                    <Building2 size={18} />
                  </div>
                  <div style={{flex:1}}><div style={{fontFamily:"Cormorant Garamond,serif",fontSize:15,fontWeight:600,color:T.text}}>{s.name}</div><div style={{fontSize:11,color:T.muted}}>{s.area}</div></div>
                  <Heart onClick={()=>toggleFav(s.id)} size={16} fill={T.gold} color={T.gold} style={{cursor:"pointer"}} />
                </div>
              ))}
            </div>
            {/* Loyalty */}
            <div style={{background:T.dark3,border:`1px solid ${T.goldBdr}`,borderRadius:14,padding:16}}>
              <div style={{fontFamily:"Cormorant Garamond,serif",fontSize:18,fontWeight:600,color:T.text,marginBottom:10,paddingBottom:8,borderBottom:`1px solid ${T.goldBdr}`}}>{t("dashboard.loyalty_points")}</div>
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                <div style={{fontFamily:"Cormorant Garamond,serif",fontSize:36,fontWeight:600,color:T.goldLt}}>{loyaltyPts.toLocaleString("en-IN")}</div>
                <div><div style={{fontSize:12,color:T.muted}}>points earned</div><div style={{fontSize:11.5,color:T.subtle}}>1 pt = ₹1 value</div></div>
              </div>
              <BtnGold full size="sm" onClick={()=>navigate("/loyalty")} style={{display:"flex",alignItems:"center",gap:6,justifyContent:"center"}}>
                {t("loyalty.rewards")} <ChevronRight size={16} />
              </BtnGold>
            </div>
          </div>
        </div>
      </div>
      {topupModal && (
        <div onClick={e => e.target === e.currentTarget && setTopupModal(false)} style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(0,0,0,.85)", backdropFilter:"blur(5px)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:T.dark3, border:`1px solid ${T.goldBdrS}`, borderRadius:18, width:"90%", maxWidth:380, padding:"28px 24px" }}>
            <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:22, fontWeight:600, color:T.text, marginBottom:18 }}>Top Up Wallet</div>
            <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
              {[100, 250, 500, 1000, 2000, 5000].map(amt => (
                <button key={amt} onClick={() => setTopupAmt(amt)} style={{
                  padding:"8px 16px", borderRadius:10, fontSize:13, cursor:"pointer", fontFamily:"inherit",
                  background: topupAmt === amt ? T.goldDim : "transparent",
                  border: `1px solid ${topupAmt === amt ? T.gold : T.goldBdr}`,
                  color: topupAmt === amt ? T.goldLt : T.muted,
                }}>₹{amt}</button>
              ))}
            </div>
            <input type="number" value={topupAmt} onChange={e => setTopupAmt(Number(e.target.value))} min={100} max={50000}
              style={{ width:"100%", padding:"10px 14px", background:T.dark4, border:`1.5px solid ${T.goldBdr}`, borderRadius:10, color:T.text, fontSize:16, fontFamily:"Cormorant Garamond,serif", fontWeight:600, outline:"none", marginBottom:18, textAlign:"center" }} />
            <div style={{ display:"flex", gap:10 }}>
              <BtnGhost onClick={() => setTopupModal(false)}>Cancel</BtnGhost>
              <BtnGold full disabled={toppingUp || topupAmt < 100} onClick={() => {
                doTopup(topupAmt, {
                  onSuccess: (res) => { show(res.data?.message || `₹${topupAmt} added to wallet!`); setTopupModal(false); },
                  onError: (err) => show(err.message || "Top-up failed"),
                });
              }}>
                {toppingUp ? <><Spinner size={16}/> Processing...</> : `Add ₹${topupAmt}`}
              </BtnGold>
            </div>
          </div>
        </div>
      )}
      <Footer/>
      <Toast msg={msg} visible={visible}/>
    </>
  );
}

